// Souhrn nad tabulkou Do košíku: poměr celého e-shopu na počítači, mobilu,
// v Česku a na Slovensku. Klik na kartu → týdenní trend segmentu proti celku.

import { type WeekPoint, WeeklyRateChart, fmtPct } from '@/components/analytics/WeeklyRateChart';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useState } from 'react';

type Range = { startDate: string; endDate: string };
type Segment = 'desktop' | 'mobile' | 'cz' | 'sk';
type Counts = { views: number; adds: number };
type Summary = Record<Segment, Counts> & { skMissing: boolean };
type ShopSeries = Record<Segment | 'total', WeekPoint[]>;

const SEGMENTY: { key: Segment; label: string; color: string }[] = [
  { key: 'desktop', label: 'Na počítači', color: '#3b82f6' },
  { key: 'mobile', label: 'Na mobilu', color: '#f97316' },
  { key: 'cz', label: 'Česko', color: '#10b981' },
  { key: 'sk', label: 'Slovensko', color: '#8b5cf6' },
];

const rateOf = (c: Counts) => (c.views > 0 ? (c.adds / c.views) * 100 : null);
const fmtNum = (n: number) => new Intl.NumberFormat('cs-CZ').format(Math.round(n));

export function CartRateSummary({ range }: { range: Range }) {
  const [data, setData] = useState<Summary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<Segment | null>(null);

  useEffect(() => {
    let zruseno = false;
    setData(null);
    setErr(null);
    const params = new URLSearchParams({ from: range.startDate, to: range.endDate });
    apiFetch<Summary>(`/api/analytics/cart-rate/summary?${params.toString()}`)
      .then((r) => {
        if (!zruseno) setData(r);
      })
      .catch((e: unknown) => {
        if (!zruseno) setErr(e instanceof ApiError ? e.describe() : 'Souhrn se nenačetl');
      });
    return () => {
      zruseno = true;
    };
  }, [range.startDate, range.endDate]);

  if (err) return <p className="text-sm text-danger mb-4">{err}</p>;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {SEGMENTY.map((s) => {
          const c = data?.[s.key];
          const neni = s.key === 'sk' && data?.skMissing;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => !neni && setOpen(s.key)}
              disabled={!!neni}
              className="bg-surface rounded-xl shadow-card p-4 text-left cursor-pointer hover:shadow-card-hover transition-shadow disabled:cursor-default disabled:opacity-60"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                {s.label}
              </div>
              <div className="text-2xl font-bold text-text mt-1">
                {!data ? '…' : neni ? 'nenapojeno' : fmtPct(c ? rateOf(c) : null)}
              </div>
              {c && !neni && (
                <div className="text-[11px] text-text-muted mt-0.5">
                  {fmtNum(c.adds)} přidání z {fmtNum(c.views)} zobrazení
                </div>
              )}
            </button>
          );
        })}
      </div>
      {open && <ShopTrendModal segment={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function ShopTrendModal({ segment, onClose }: { segment: Segment; onClose: () => void }) {
  useEscapeKey(onClose);
  const [weeks, setWeeks] = useState(26);
  const [series, setSeries] = useState<ShopSeries | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const seg = SEGMENTY.find((s) => s.key === segment);

  useEffect(() => {
    let zruseno = false;
    setSeries(null);
    setErr(null);
    apiFetch<{ series: ShopSeries }>(`/api/analytics/cart-rate/shop-trend?weeks=${weeks}`)
      .then((r) => {
        if (!zruseno) setSeries(r.series);
      })
      .catch((e: unknown) => {
        if (!zruseno) setErr(e instanceof ApiError ? e.describe() : 'Načtení trendu selhalo');
      });
    return () => {
      zruseno = true;
    };
  }, [weeks]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-4xl my-8 bg-surface rounded-2xl shadow-modal p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-bold text-text">Přidání do košíku — {seg?.label}</h2>
            <p className="text-xs text-text-muted">
              Celý e-shop po týdnech; šedá přerušovaná = všechna zařízení a oba e-shopy dohromady.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {[12, 26, 52].map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWeeks(w)}
                className={`text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${
                  weeks === w
                    ? 'bg-primary text-white border-primary'
                    : 'border-border text-text-secondary hover:bg-surface-hover'
                }`}
              >
                {w} týdnů
              </button>
            ))}
            <button
              type="button"
              onClick={onClose}
              aria-label="Zavřít"
              className="ml-2 text-text-muted hover:text-text text-xl leading-none cursor-pointer"
            >
              ✕
            </button>
          </div>
        </div>
        {err && <p className="text-sm text-danger mb-3">{err}</p>}
        {!series && !err && <p className="text-sm text-text-muted py-16 text-center">Načítám…</p>}
        {series && seg && (
          <WeeklyRateChart
            lines={[
              { label: 'Celý e-shop', color: '#94a3b8', points: series.total, muted: true },
              { label: seg.label, color: seg.color, points: series[segment] },
            ]}
          />
        )}
      </div>
    </div>
  );
}
